import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const solutions = [
  {
    title: "Preventive AC Maintenance",
    description: "Regular maintenance helps identify potential issues before they become costly failures. Our technicians perform detailed inspections and servicing to maintain peak system efficiency.",
    includes: [
      "Cleaning of evaporator and condenser coils",
      "Inspection of refrigerant levels",
      "Electrical component testing",
      "Thermostat calibration",
      "Airflow and duct inspection",
      "System performance evaluation"
    ],
    icon: "Shield"
  },
  {
    title: "Corrective Maintenance",
    description: "When air conditioning systems experience operational issues, our technical team provides quick diagnostics and repair services to restore functionality and minimize downtime.",
    includes: [
      "Compressor repairs",
      "Refrigerant leak detection and refilling",
      "Electrical fault troubleshooting",
      "Replacement of faulty components",
      "Cooling performance restoration"
    ],
    icon: "Wrench"
  },
  {
    title: "Annual Maintenance Contracts (AMC)",
    description: "Our Annual Maintenance Contracts provide scheduled inspections and servicing to ensure uninterrupted system performance. This proactive service model helps maintain efficiency while preventing unexpected failures.",
    includes: [
      "Scheduled maintenance visits",
      "Priority technical support",
      "Reduced repair costs",
      "Improved energy efficiency",
      "Extended equipment lifespan"
    ],
    icon: "FileCheck"
  },
  {
    title: "System Cleaning and Optimization",
    description: "Over time, dust and debris can reduce the efficiency of AC systems. Our comprehensive cleaning services improve airflow and cooling performance while maintaining healthy indoor air quality.",
    includes: [
      "Air filter replacement or cleaning",
      "Duct cleaning and inspection",
      "Drain line cleaning",
      "Coil and fan cleaning"
    ],
    icon: "Wind"
  }
];

const ACSolutions = () => {
  return (
    <section className="py-24 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative w-full max-w-4xl mx-auto mb-12 overflow-hidden"
          >
            <Image 
              src="/assets/images/AC-page/AC-1080px-by-1080px-2.jpg" 
              alt="AC Solutions" 
              className="w-full h-64 md:h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#4D4C4A]/70 to-[#91756A]/70"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <h4 className="text-3xl md:text-4xl font-bold text-white italic tracking-tighter uppercase">
                Professional <span className="text-[#91756A]">AC</span> Solutions
              </h4>
            </div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-sm font-bold uppercase tracking-[0.4em] text-[#91756A] mb-4"
          >
            Our AC Maintenance Solutions
          </motion.h2>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold text-[#4D4C4A] italic tracking-tighter"
          >
            Comprehensive Care for Your HVAC Systems.
          </motion.h3>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {solutions.map((solution, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="bg-white p-10 shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-500 group"
            >
              <div className="flex items-start gap-6 mb-8">
                <div className="w-14 h-14 bg-[#4D4C4A] flex items-center justify-center shrink-0 group-hover:bg-[#91756A] transition-colors duration-300">
                  <Icon name={solution.icon} size={28} className="text-white" />
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-[#4D4C4A] italic tracking-tight uppercase mb-4">
                    {solution.title}
                  </h4>
                  <p className="text-slate-500 leading-relaxed mb-6">
                    {solution.description}
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {solution.includes.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-[#91756A] rounded-full"></div>
                    <span className="text-sm text-slate-600 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ACSolutions;